import { useMemo } from "react";
import useSWR from "swr";
import { format, subDays, eachDayOfInterval } from "date-fns";

import { fetchAccountStats, fetchPnlHistory } from "lib/indexer/client";
import { bigNumberify, expandDecimals } from "lib/numbers";
import { USD_DECIMALS } from "config/factors";
import { CONFIG_UPDATE_INTERVAL } from "lib/timeConstants";

const MAX_DAILY_VOLUME = expandDecimals(220_000n, USD_DECIMALS);
const AGG_14_DAYS_VOLUME = expandDecimals(1_200_000n, USD_DECIMALS);
const AGG_ALL_TIME_VOLUME = expandDecimals(3_500_000n, USD_DECIMALS);

export function useIsLargeAccountData(account?: string) {
  const { data, error, isLoading } = useIsLargeAccountVolumeStats({ account });

  const isLargeAccount = useMemo(() => {
    if (!data || isLoading || error) return undefined;

    const { totalVolume, dailyVolume } = data;

    const maxDailyVolume = dailyVolume.reduce((max, day) => (day.volume > max ? day.volume : max), 0n);

    const last14DaysVolume = dailyVolume.slice(-14).reduce((acc, day) => acc + day.volume, 0n);

    return (
      maxDailyVolume >= MAX_DAILY_VOLUME || last14DaysVolume >= AGG_14_DAYS_VOLUME || totalVolume >= AGG_ALL_TIME_VOLUME
    );
  }, [data, isLoading, error]);

  return isLargeAccount;
}

function useIsLargeAccountVolumeStats(params: { account?: string }) {
  const { account } = params;

  const now = new Date();
  const date30dAgo = subDays(now, 30);
  const last30dList = eachDayOfInterval({ start: date30dAgo, end: now });

  const { data, error, isLoading } = useSWR<{
    totalVolume: bigint;
    dailyVolume: { date: string; volume: bigint }[];
  }>(account ? ["useIsLargeAccountVolumeStats", account] : null, {
    refreshInterval: CONFIG_UPDATE_INTERVAL,
    fetcher: async () => {
      const [stats, pnlHistory] = await Promise.all([
        fetchAccountStats(account!),
        fetchPnlHistory(account!, { buckets: 30, period: "day" }),
      ]);

      const totalVolume = bigNumberify(stats.volumeUsd) ?? 0n;

      const bucketByDate = new Map<string, bigint>();
      for (const b of pnlHistory.buckets) {
        const date = new Date(b.bucketTs * 1000).toISOString().slice(0, 10);
        bucketByDate.set(date, bigNumberify(b.volumeUsd) ?? 0n);
      }

      const dailyVolume = last30dList.map((day) => {
        const date = format(day, "yyyy-MM-dd");
        return { date, volume: bucketByDate.get(date) ?? 0n };
      });

      return { totalVolume, dailyVolume };
    },
  });

  return useMemo(() => ({ data, error, isLoading }), [data, error, isLoading]);
}
